import { useSupportSheet } from "../../components/SupportSheet";
import { memo, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  GradientText,
  Layout,
  SuccessIcon,
  SupportIcon,
} from "lola-framework-ui-test";
import { useStore } from "../../store/globalState";
import { PAYOUT_CLAIM_ROUTES } from "../../constants";
import {
  getPayoutClaimMockReceipt,
  payoutClaimMockSenderName,
} from "../../gallery/payoutClaimMock";
import { amountFormatter, formatHeroAmount, formatLabeledAmount } from "../../utils";
import {
  StatusCircleIcon,
  StatusFailedIcon,
  StatusPendingIcon,
} from "../../assets/icons";
import {
  STATUS_TONE_COLOR,
  toStatusPresentation,
  type StatusTone,
} from "../../utils/statusPresentation";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";

const statusIcon = (tone: StatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <StatusFailedIcon size={10} color={color} />;
    case "initial":
      return <StatusPendingIcon size={10} color={color} />;
    case "progress":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

const formatReceiptDate = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatReceiptDateTime = (isoDate: string): string => {
  const date = new Date(isoDate);
  return `${formatReceiptDate(date)}\n${date.toLocaleTimeString()}`;
};

const availableReceiptDate = (isoDate: string): string => {
  const result = new Date(isoDate);
  let added = 0;
  while (added < 5) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return formatReceiptDate(result);
};

const money = (amount: number | undefined, currency: string): string =>
  formatLabeledAmount(amount ?? 0, currency);

/**
 * Payout-claim receipt (gallery). Resolves mock operation data by `:operationId`.
 */
export const ReceiptSummaryPage = memo(() => {
  const { t } = useTranslation("payout-claim");
  const { theme } = useStore();
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("payout-claim");
  const location = useLocation();
  const { operationId: operationIdParam } = useParams<{ operationId: string }>();

  const colorConfig = theme.whiteView;
  const { bodyCopy, dropzoneColors, footerColor } = colorConfig;

  const operation = useMemo(
    () => getPayoutClaimMockReceipt(operationIdParam, location.pathname),
    [operationIdParam, location.pathname]
  );

  const renderContent = () => {
    const source = operation.sourceCurrency;
    const destination = operation.destinationCurrency;
    const { tone, labelKey } = toStatusPresentation(operation.status);
    const statusColor = STATUS_TONE_COLOR[tone];

    const { prefix, suffix, intPart: recvInt, decPart: recvDec } = formatHeroAmount(
      operation.receiveAmount,
      destination
    );

    const summaryItems: {
      label: string;
      value: string;
      isBold?: boolean;
      statusColor?: string;
      icon?: React.ReactNode;
    }[] = [
      {
        label: t("receipt.status"),
        value: t(labelKey),
        isBold: true,
        statusColor,
        icon: statusIcon(tone, statusColor),
      },
      {
        label: t("receipt.transferAmount"),
        value: money(operation.sendAmount, source),
      },
      {
        label: t("receipt.transactionFee"),
        value: money(operation.transactionFee, source),
      },
      {
        label: t("receipt.exchangeRate"),
        value: `${amountFormatter(operation.exchangeRate, destination, {
          country: "en-US",
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        })} ${destination} / ${source}`,
      },
      {
        label: t("receipt.totalPayment"),
        value: money(operation.totalCost, source),
      },
      {
        label: t("receipt.receiving"),
        value: money(operation.receiveAmount, destination),
      },
      {
        label: t("receipt.fundingFee"),
        value: money(
          operation.fundingFee ?? operation.fee ?? operation.totalFee,
          source
        ),
      },
      {
        label: t("receipt.transferTaxes"),
        value: money(operation.taxFee, source),
      },
      {
        label: t("receipt.sender"),
        value: payoutClaimMockSenderName,
      },
      {
        label: t("receipt.transactionDate"),
        value: formatReceiptDateTime(operation.createdAt),
      },
      {
        label: t("receipt.dateAvailable"),
        value: availableReceiptDate(operation.createdAt),
      },
    ];

    return (
      <section className="receipt-summary-container">
        <section className="summary-section">
          <section className="summary-title">
            <BodyCopy textColor={bodyCopy}>
              {t("receipt.youReceived")}
            </BodyCopy>
            <GradientText as="h1">
              {prefix}{recvInt}.<label>{recvDec}</label>
              {suffix ? (
                <span className="receipt-hero-currency">{suffix}</span>
              ) : null}
            </GradientText>
            <BodyCopy
              className="receipt-trx-caption"
              textColor={bodyCopy}
              aria-label={t("receipt.transactionId")}
            >
              {operation.operationId.slice(0, 8).toUpperCase()}
            </BodyCopy>
          </section>
          <section className="summary-info">
            {summaryItems.map(({ label, value, isBold, statusColor: color, icon }, index) => (
              <SummaryInfoItem
                key={index}
                status={label}
                value={value}
                isBold={isBold}
                statusColor={color}
                icon={icon}
              />
            ))}
          </section>
        </section>
        <section className="receipt-summary-disclaimers">
          <BodyCopy className="footer-text" textColor={footerColor}>
            {t("receipt.footerNote")}
          </BodyCopy>
        </section>
      </section>
    );
  };

  return (
    <>
    <AuraLayout
      colorConfig={colorConfig}
      className="need-help-header receipt-summary-section"
    >
      <Layout.Header>
        <Navbar
          title={t("common.needHelp")}
          align="right"
          onBackClick={() => navigate(PAYOUT_CLAIM_ROUTES.TRANSACTIONS)}
          onIconClick={openSupport}
          icon={<SupportIcon size={35} colors={dropzoneColors} />}
        />
      </Layout.Header>
      <Layout.Content isOverflowauto className="receipt-summary-content">
        <ScrollFadeIndicator color={bodyCopy} />
        {renderContent()}
      </Layout.Content>
    </AuraLayout>
    {supportSheet}
  </>
  );
});

interface SummaryInfoItemProps {
  isBold?: boolean;
  statusColor?: string;
  status: string;
  value: string | React.ReactNode;
  icon?: React.ReactNode;
}

const SummaryInfoItem: React.FC<SummaryInfoItemProps> = ({
  isBold,
  status,
  value,
  statusColor,
  icon,
}) => (
  <section className={`summary-info-item ${isBold ? "bold" : ""} `}>
    <p>{status}</p>
    <p
      className={icon ? "summary-info-status" : undefined}
      style={{
        ...(statusColor ? { color: statusColor } : undefined),
        whiteSpace: "pre-line",
        textAlign: "right",
      }}
    >
      {icon}
      {value}
    </p>
  </section>
);
